// FILE: ActiveSubagentsIndicator.tsx
// Purpose: Indicator above the composer surfacing the subagents still working for
//          this thread — and the delegations that already answered — so a thread
//          whose own turn ended does not read as finished while work continues.
// Layer: Chat UI component
// Depends on: session-logic active subagent task state

import { memo, useMemo } from "react";
import type { ThreadId } from "@taskin-code/contracts";
import { RiRobot3Line } from "react-icons/ri";

import type { ActiveSubagentTask, ActiveSubagentTasksState } from "../../session-logic";
import { LoaderIcon } from "~/lib/icons";
import { cn } from "~/lib/utils";
import { Popover, PopoverClose, PopoverPopup, PopoverTrigger } from "../ui/popover";
import {
  buildActiveSubagentListItems,
  earliestStartedAt,
  summarizeActiveSubagents,
} from "./ActiveSubagentsIndicator.logic";
import { WorkingTimer } from "./WorkingTimer";

interface ActiveSubagentsIndicatorProps {
  activeSubagentTasks: ActiveSubagentTasksState | null;
  /**
   * Delegations that already answered and are waiting to be read. Kept separate
   * from the running ones so neither is reported as the other.
   */
  awaitingParentTasks?: ReadonlyArray<ActiveSubagentTask> | undefined;
  onScrollToBottom: () => void;
  onOpenThread?: ((threadId: ThreadId) => void) | undefined;
}

/** Shared empty list, so a missing group does not invalidate the memos below. */
const NO_TASKS: ReadonlyArray<ActiveSubagentTask> = [];

export const ActiveSubagentsIndicator = memo(function ActiveSubagentsIndicator({
  activeSubagentTasks,
  awaitingParentTasks,
  onScrollToBottom,
  onOpenThread,
}: ActiveSubagentsIndicatorProps) {
  const runningTasks = activeSubagentTasks?.tasks ?? NO_TASKS;
  const awaitingTasks = awaitingParentTasks ?? NO_TASKS;
  const items = useMemo(
    () => buildActiveSubagentListItems([...runningTasks, ...awaitingTasks]),
    [awaitingTasks, runningTasks],
  );
  const summary = useMemo(
    () => summarizeActiveSubagents(runningTasks, awaitingTasks),
    [awaitingTasks, runningTasks],
  );
  const oldestStartedAt = useMemo(() => earliestStartedAt(runningTasks), [runningTasks]);
  const isRunning = runningTasks.length > 0;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={summary}
            className={cn(
              "flex w-full items-center gap-1.5 overflow-hidden rounded-t-2xl border border-b-0 border-border/60 bg-card px-2.5 py-2 text-left text-[12px] transition-colors hover:bg-muted/40",
              // Coloured rather than muted: this line is the only thing on screen
              // saying the work is not over, and grey lost that argument against
              // the settled turn above it.
              isRunning
                ? "text-violet-600 dark:text-violet-300/90"
                : "text-amber-600 dark:text-amber-300/90",
            )}
          >
            <RiRobot3Line className="size-3.5 shrink-0" />
            {isRunning ? <LoaderIcon className="size-3 shrink-0 animate-spin" /> : null}
            <span className="min-w-0 flex-1 truncate">{summary}</span>
            {oldestStartedAt ? (
              <span className="shrink-0 tabular-nums opacity-80">
                <WorkingTimer createdAt={oldestStartedAt} />
              </span>
            ) : null}
          </button>
        }
      />
      <PopoverPopup align="start" className="w-72 max-w-[90vw]" side="top">
        <ul className="flex flex-col gap-0.5">
          {items.map((item) => (
            <li key={item.key}>
              <PopoverClose
                render={
                  <button
                    type="button"
                    className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
                    onClick={() => {
                      // A delegation is a thread of its own, so its row opens it.
                      // The in-session shapes have nothing to open: their work is
                      // in this transcript, so the row takes the user there.
                      if (item.threadId && onOpenThread) {
                        onOpenThread(item.threadId);
                        return;
                      }
                      onScrollToBottom();
                    }}
                  >
                    <RiRobot3Line
                      className={cn(
                        "mt-0.5 size-3.5 shrink-0",
                        item.awaitingParent
                          ? "text-amber-600 dark:text-amber-300/90"
                          : "text-violet-600/80 dark:text-violet-300/80",
                      )}
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[12px] text-foreground/90">{item.label}</span>
                      {item.detail ? (
                        <span className="truncate text-[11px] text-muted-foreground/70">
                          {item.detail}
                        </span>
                      ) : null}
                      <span className="text-[11px] text-muted-foreground/60">
                        {item.awaitingParent ? (
                          "Entregó su informe"
                        ) : item.startedAt ? (
                          <>
                            Trabajando · <WorkingTimer createdAt={item.startedAt} />
                          </>
                        ) : (
                          "Trabajando"
                        )}
                      </span>
                    </span>
                    <span className="mt-0.5 shrink-0 text-[10px] text-muted-foreground/60">
                      {item.threadId ? "Open" : "Show"}
                    </span>
                  </button>
                }
              />
            </li>
          ))}
        </ul>
      </PopoverPopup>
    </Popover>
  );
});

export type { ActiveSubagentsIndicatorProps };
